import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SensorScope } from './contracts';
import {
  migrationOverviewSchema,
  migrationSaveSchema,
  type MigrationOverview,
  type MigrationRecord,
} from './migrationContracts';
import { AlertApiError, callAlerts } from './transport';

export const migrationQueryKey = ['unified-alert-migration'] as const;
export type PrepareMigration = SensorScope & { expectedFingerprint: string };
export type ActivateMigration = PrepareMigration & { expectedVersion: number };
export interface MigrationService {
  get: (scope: SensorScope) => Promise<MigrationOverview>;
  prepare: (command: PrepareMigration) => Promise<{ migration: MigrationRecord }>;
  activate: (command: ActivateMigration) => Promise<{ migration: MigrationRecord }>;
}

function checkScope<T extends { orgId: string; sensorId: string }>(
  value: T,
  scope: SensorScope
): T {
  if (value.orgId !== scope.orgId || value.sensorId !== scope.sensorId)
    throw new AlertApiError(
      'Alarmopsætningen tilhører en anden enhed. Opdatér og prøv igen.',
      'invalid-response'
    );
  return value;
}

export const migrationService: MigrationService = {
  async get(scope) {
    const result = await callAlerts('GET_UNIFIED_ALERT_MIGRATION', scope, migrationOverviewSchema);
    if (result.plan) checkScope(result.plan, scope);
    if (result.migration) checkScope(result.migration, scope);
    return result;
  },
  async prepare(command) {
    const result = await callAlerts(
      'PREPARE_UNIFIED_ALERT_MIGRATION',
      command,
      migrationSaveSchema
    );
    checkScope(result.migration, command);
    if (
      result.migration.status !== 'prepared' ||
      result.migration.sourceFingerprint !== command.expectedFingerprint
    )
      throw new AlertApiError(
        'Overførslen kunne ikke bekræftes. Hent den seneste opsætning.',
        'invalid-response'
      );
    return result;
  },
  async activate(command) {
    const result = await callAlerts(
      'ACTIVATE_UNIFIED_ALERT_MIGRATION',
      command,
      migrationSaveSchema
    );
    checkScope(result.migration, command);
    if (
      result.migration.status !== 'active' ||
      result.migration.sourceFingerprint !== command.expectedFingerprint ||
      result.migration.version <= command.expectedVersion
    )
      throw new AlertApiError(
        'Aktiveringen kunne ikke bekræftes. Hent den seneste opsætning.',
        'invalid-response'
      );
    return result;
  },
};

export function useAlertMigration(scope: SensorScope | undefined, service = migrationService) {
  return useQuery({
    queryKey: [...migrationQueryKey, scope?.orgId, scope?.sensorId],
    queryFn: () => {
      if (!scope) throw new Error('Vælg en enhed for at hente alarmopsætningen.');
      return service.get(scope);
    },
    enabled: Boolean(scope),
    staleTime: 30_000,
    retry: false,
  });
}

export function useMigrationActions(service = migrationService) {
  const client = useQueryClient();
  async function refresh(scope: SensorScope) {
    await Promise.all([
      client.invalidateQueries({ queryKey: [...migrationQueryKey, scope.orgId, scope.sensorId] }),
      client.invalidateQueries({ queryKey: ['unified-alerts'] }),
      client.invalidateQueries({ queryKey: ['unified-heartbeats'] }),
    ]);
  }
  return {
    async prepare(command: PrepareMigration) {
      const result = await service.prepare(command);
      await refresh(command);
      return result;
    },
    async activate(command: ActivateMigration) {
      const result = await service.activate(command);
      await refresh(command);
      return result;
    },
  };
}
